import React, { Component, ReactNode } from 'react';
import { RootState } from '../../../redux/store';
import { changeListVariable, changeListVariableIsUpdate, clearListVariable } from '../../../redux/actions/ListVariableActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { List, ListColumnField, ListField } from '../../../domain/list/ListDomains';
import { query, repository, subscribe } from '@trlogic/client-js';
import CollectionNames from '../../../constant/CollectionNames';
import { FormicaProps, withFormica } from 'helpers/withFormica';
import { InstanceContainer } from '../../../config/binding/InstanceBinding';
import Injectables from '../../../constant/Injectables';
import { DataTable, DataTableSelectionChangeParams } from 'primereact/datatable';
import CustomLoading from '../../custom/CustomLoading';
import FormicaLabel from '../../custom/FormicaLabel';
import FormicaSearchSelectBox, { FormicaSelectBoxItem } from '../../custom/FormicaSearchSelectBox';
import { Column, ColumnProps } from 'primereact/column';
import {
  ArchiosExpressionProfile,
  ExpressionBuilderView,
  ExpressionContextMenuComponent,
  ExpressionTokenComponent,
  StandardExpressionBuilderView,
  Syntax,
  TokenVariable,
  VariableToken,
} from '@trlogic/ui-pack';
import { ListVariableState } from '../../../redux/reducers/ListVariableReducer';
import VariableType from 'constant/VariableType';
import { generateUUIDV4 } from '../../../util/Generator';
import { TransactionJsonModel } from 'domain/stream/transaction/TransactionDomains';
import RoleHelper from '../../../RoleHelper';
import { RadioButton } from 'primereact/radiobutton';
import DataType from '../../../constant/DataType';
import ExpressionBridge from '../../../util/ExpressionBridge';
import ServiceContainer from '../../../config/ServiceContainer';
import { ServiceNames } from '../../../constant/ServiceNames';
import LanguageUtil from '../../../util/LanguageUtil';
import EmptyComponent from '../../custom/empty-components/EmptyComponent';
import ComponentName from '../../../constant/ComponentName';
import { EnrichmentBase } from '../../../domain/enrichment/EnrichmentDomain';
import _ from 'lodash';
import { ExpressionUtil } from '../../../util/ExpressionUtil';

class EnrichmentListEditorComponent extends Component<Props, State> {
  public state = new State();

  private listSubscription: subscribe.Subscription | undefined;
  private transactionSubscription: subscribe.Subscription | undefined;
  private enrichmentSubscription: subscribe.Subscription | undefined;
  private computation: subscribe.Computation | undefined;
  private repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);

  private readonly expressionBridge: ExpressionBridge = ServiceContainer.getInstance().get(ServiceNames.EXPRESSION_BRIDGE);

  public render(): ReactNode {
    const { selectedList, loading, expressionView, expression } = this.state;
    const {
      listVariable: { listVariable },
    } = this.props;
    if (loading) {
      return <CustomLoading />;
    }
    if (expressionView == undefined) {
      return null;
    }
    return (
      <div className={'grid grid-cols-5 w-full h-full'}>
        <div className={'col-span-1 flex flex-col border-r border-f-black-5 px-5 py-5 gap-y-4'}>
          <div className={'flex flex-col'}>
            <FormicaLabel label={LanguageUtil.get({ id: 'common.label.channel', defaultMessage: 'Channel' })} />
            <FormicaSearchSelectBox
              options={this.getTransactionOptions()}
              onChange={(e: FormicaSelectBoxItem) => {
                this.onChangeTransaction(e);
              }}
              value={listVariable.transaction}
              rounded={true}
              disabled={!RoleHelper.canEdit('EnrichmentBasic')}
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.channel', defaultMessage: 'Channel' })}
              data-testid={`${ComponentName.LIST_VARIABLE}_channel_select`}
            />
          </div>
          <div className={'flex flex-col'}>
            <FormicaLabel label={LanguageUtil.get({ id: 'common.label.list', defaultMessage: 'List' })} />
            <FormicaSearchSelectBox
              options={this.getListOptions()}
              onChange={(e: FormicaSelectBoxItem) => {
                this.onChangeList(e);
              }}
              rounded={true}
              value={listVariable.list}
              disabled={!RoleHelper.canEdit('EnrichmentBasic')}
              placeholder={LanguageUtil.get({ id: 'common.label.list', defaultMessage: 'List' })}
              data-testid={`${ComponentName.LIST_VARIABLE}_list_select`}
            />
          </div>
        </div>
        <div className={'col-span-4 flex flex-col w-full h-full px-5 py-5 gap-y-2 overflow-y-scroll max-h-[calc(100vh-17.5rem)]'}>
          <FormicaLabel label={LanguageUtil.get({ id: 'common.label.key', defaultMessage: 'Key' })} />
          <div
            className={'flex flex-col shrink-0 w-full border-[1px] border-[#E0E4E8] overflow-scroll rounded-[4px] px-5 py-5 bg-main-gray'}
            id='expression_builder_key'
          >
            {expression && expressionView && (
              <div id='px-5 py-5 bg-slate-300'>
                <ExpressionTokenComponent syntax={expressionView.getModel()} view={expressionView} />
                <ExpressionContextMenuComponent context={expressionView.getContext()} close={this.onClose} />
              </div>
            )}
          </div>
          <div className={'flex flex-col w-full h-full space-y-2 '}>
            <FormicaLabel label={LanguageUtil.get({ id: 'common.label.field', defaultMessage: 'Field' })} />
            {selectedList == undefined ? (
              <div className='items-center  flex justify-center w-full h-full gap-x-4 px-1'>
                <EmptyComponent createDisabled={true} type={ComponentName.LIST} />
              </div>
            ) : (
              <DataTable
                className={'w-full h-full !text-f-black-3 border-[1px] border-f-black-5 rounded-[10px] !font-formica !text-sm '}
                size='small'
                dataKey={'name'}
                reorderableColumns
                responsiveLayout='scroll'
                paginatorClassName='float-right'
                stripedRows
                filterDisplay='menu'
                onSelectionChange={(e: DataTableSelectionChangeParams) => this.onChangeSelectedField(e)}
                resizableColumns
                columnResizeMode='fit'
                dragSelection
                selectionMode={'single'}
                selection={listVariable?.field || []}
                value={selectedList?.fields}
              >
                {this.getColumns().map((c: ColumnProps, i) => (
                  <Column key={i} field={c.field} className={'!h-[52px] text-xs 2xl:text-md'} bodyClassName={'!h-[52px]'} header={c.header} {...c} />
                ))}
              </DataTable>
            )}
          </div>
        </div>
      </div>
    );
  }

  public componentDidMount() {
    this.startTracker();
  }

  private getColumns = (): ColumnProps[] => {
    const { selectedField } = this.state;
    return [
      {
        field: 'radiobutton',
        header: '',
        headerClassName: 'h-[40px] xl:h-[52px]',
        className: 'w-[2%]',
        body: (record: List) => {
          return <RadioButton onClick={(event) => event.stopPropagation()} style={{ pointerEvents: 'none' }} checked={selectedField == record.name} />;
        },
      },
      {
        field: 'name',
        header: 'Name',
        className: 'w-1/4',
        body: (record: List) => {
          return (
            <p className={'text-left flex items-center'} data-testid={`${ComponentName.LIST_VARIABLE}_field_name_${record.name}`}>
              {' '}
              {record.name}{' '}
            </p>
          );
        },
      },
      {
        field: 'type',
        header: 'Data Type',
        className: 'w-1/4',

        body: (record: List) => {
          return <p className={'text-left flex items-center'}> {record.type} </p>;
        },
      },
      {
        field: 'description',
        header: 'Description',
        className: 'w-1/4',
        body: (record: List) => {
          return <p className={'flex justify-start items-center'}>{record.description}</p>;
        },
      },
    ];
  };

  private buildData = async () => {
    const {
      listVariable: { listVariable, isUpdate },
    } = this.props;
    const { lists } = this.state;
    this.state.expressionView?.terminate();
    let expression: Syntax;
    if (isUpdate) {
      expression = listVariable.key;
      const selectedList = lists.find((list) => list.name == listVariable.list);
      this.setState({ selectedList: selectedList, selectedField: listVariable.field });
    } else {
      expression = {
        id: generateUUIDV4(),
        value: { type: 'TokenGroup' },
        children: [],
      };
    }
    const variables: Record<string, TokenVariable[]> = await this.prepareVariables(listVariable.transaction);
    await this.expressionBridge.start(expression, variables);
    const expressionView: ExpressionBuilderView = new StandardExpressionBuilderView({
      el: '#expression_builder_key',
      model: expression,
      profile: new ArchiosExpressionProfile(),
      returnType: { dataType: DataType.STRING },
      onCtrlToken: ExpressionUtil.onNavigateToken,
    });
    expressionView.getContext().setVariables(variables);
    expressionView.onChange(this.onChangeSyntax as any);
    this.setState({ expressionView, expression }, () => expressionView.initialize());
  };

  private prepareVariables = async (transaction: string): Promise<Record<string, TokenVariable[]>> => {
    const { transactions } = this.state;
    const selectedTransaction = transactions.find((t) => t.name == transaction);
    if (selectedTransaction == undefined) return {};
    const eventVariables: any[] = selectedTransaction.variables.map((v) => ({
      type: VariableType.EVENT_VARIABLE,
      variable: {
        type: 'Common Variable',
        variableType: VariableType.EVENT_VARIABLE,
        properties: v.properties,
        name: v.name,
        description: v.description,
        args: {
          event: selectedTransaction.name,
        },
      },
    }));
    const enrichmentVariables: Record<
      Exclude<VariableType, VariableType.EVENT_VARIABLE | VariableType.ACTOR_VARIABLE | VariableType.PREVIOUS_EVENT_VARIABLE>,
      TokenVariable<VariableToken>[]
    > = _.groupBy(
      ((await this.repository.findMany(query.QueryBuilder.select().from(CollectionNames.ENRICHMENTS).build())) as EnrichmentBase[]).map((e) => ({
        type: e.type,
        variable: {
          type: 'Common Variable',
          variableType: e.type,
          name: e.name,
          properties: e.properties,
          args: {},
        },
      })),
      (e: any) => e.type,
    ) as Record<any, any[]>;
    return {
      [VariableType.EVENT_VARIABLE]: eventVariables,
      ...enrichmentVariables,
    };
  };

  public async componentWillUnmount() {
    const { expressionView } = this.state;
    expressionView?.terminate();
    await this.stopTracker();
  }

  public async componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
    const { isUpdate } = this.props;
    if (prevProps.isUpdate != isUpdate) {
      await this.resubscribeTracker();
    }
  }

  private resubscribeTracker = async () => {
    if (this.computation == undefined) {
      this.startTracker();
    } else {
      this.computation?.onStop(this.startTracker);
      await this.stopTracker();
    }
  };

  private startTracker = () => {
    const listQuery = query.QueryBuilder.select().from(CollectionNames.LISTS);
    const transactionQuery: query.SelectQueryBuilder = query.QueryBuilder.select().from(CollectionNames.TRANSACTIONS);
    const enrichmentQuery: query.Query = query.QueryBuilder.select()
      .from(CollectionNames.ENRICHMENTS)
      .select('name', 'properties', 'type', 'status', 'transaction', 'id')
      .build();
    this.listSubscription = subscribe.Formica.subscribe(CollectionNames.LISTS, listQuery.build());
    this.transactionSubscription = subscribe.Formica.subscribe(CollectionNames.TRANSACTIONS, transactionQuery.build());
    this.enrichmentSubscription = subscribe.Formica.subscribe(CollectionNames.ENRICHMENTS, enrichmentQuery);
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.listSubscription, this.transactionSubscription, this.enrichmentSubscription]);
  };
  private tracker = async () => {
    if (this.listSubscription?.ready() && this.transactionSubscription?.ready() && this.enrichmentSubscription?.ready()) {
      const listQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.LISTS).build();
      const transactionQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.TRANSACTIONS).build();
      const lists: List[] = await this.repository.findMany(listQuery);
      const transactions: TransactionJsonModel[] = await this.repository.findMany(transactionQuery);
      const selectedList = lists.find((list) => list.name == this.props.listVariable.listVariable.list);
      const selectedTransaction = this.props.listVariable.listVariable.transaction;
      if (selectedTransaction) {
        this.loadListVariable();
      }
      this.setState({ lists: lists, loading: false, selectedList, transactions }, () => {
        this.buildData();
      });
    }
  };

  private loadListVariable = () => {
    const {
      listVariable: { listVariable },
    } = this.props;
    const { transactions } = this.state;
    const selectedTransaction = transactions.find((t) => t.name == listVariable.transaction);
    this.setState({
      selectedTransaction,
      selectedField: listVariable.field,
    });
  };

  private stopTracker = async () => {
    await this.transactionSubscription?.stop();
    await this.listSubscription?.stop();
    await this.enrichmentSubscription?.stop();
    this.computation?.stop();
  };

  private getListOptions(): FormicaSelectBoxItem[] {
    const { lists } = this.state;
    return lists.map((list) => {
      return {
        label: list.name,
        value: list.name,
      };
    });
  }

  private getTransactionOptions(): FormicaSelectBoxItem[] {
    const { transactions } = this.state;
    return transactions.map((transaction) => {
      return {
        label: transaction.name,
        value: transaction.name,
      };
    });
  }

  private onChangeTransaction = (e: FormicaSelectBoxItem) => {
    const {
      listVariable: { listVariable },
      changeListVariable,
    } = this.props;
    const { transactions, expressionView } = this.state;
    listVariable.transaction = e.value;
    const transaction = transactions.find((transaction) => transaction.name == e.value);
    this.setState({ selectedTransaction: transaction }, async () => {
      if (transaction) {
        expressionView?.getContext().setVariables(await this.prepareVariables(transaction.name));
      }
    });
    changeListVariable(listVariable);
  };

  private onChangeList = (e: FormicaSelectBoxItem) => {
    const {
      listVariable: { listVariable },
      changeListVariable,
    } = this.props;
    const { lists } = this.state;
    listVariable.list = e.value;
    const list = lists.find((list) => list.name == e.value);
    this.setState({ selectedList: list });
    changeListVariable(listVariable);
  };

  private onChangeSelectedField = (key: DataTableSelectionChangeParams) => {
    const {
      listVariable: { listVariable },
      changeListVariable,
    } = this.props;
    const { selectedList } = this.state;
    listVariable.field = key.value.name;
    if (selectedList && key) {
      const value = key.value as ListColumnField;
      const field = selectedList.fields.find((f) => f.name == value.name);
      if (field) {
        listVariable.properties.dataType = field.type;
      }
    }
    this.setState({ selectedField: key.value.name });
    changeListVariable(listVariable);
  };
  private onChangeSyntax = (updatedExpression: Syntax): void => {
    const {
      listVariable: { listVariable },
      changeListVariable,
    } = this.props;
    listVariable.key = updatedExpression;
    changeListVariable(listVariable);
    this.setState({ expression: updatedExpression });
  };

  private onClose = () => {};
}

interface Props extends ReduxProps, FormicaProps {
  updated: number;
}

class State {
  lists: List[] = [];
  transactions: TransactionJsonModel[] = [];
  enrichments: EnrichmentBase[] = [];
  loading: boolean = true;
  selectedList: List | undefined;
  selectedTransaction: TransactionJsonModel | undefined;
  selectedField: string | undefined;
  expressionView: ExpressionBuilderView | undefined;
  variables: Record<string, TokenVariable[]> = {};
  expression: Syntax = {
    id: generateUUIDV4(),
    value: { type: 'TokenGroup' },
    children: [],
  };
}

const mapStateToProps = (state: RootState) => ({
  listVariable: state.listVariable as ListVariableState,
  isUpdate: state.listVariable.isUpdate,
});

const mapDispatchToProps = { changeListVariable, clearListVariable, changeListVariableIsUpdate };
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(EnrichmentListEditorComponent));
